/**
 * Which intake runtime the paperwork screens talk to — **the switch** (M8.7).
 *
 * A "runtime" is the trio, because the three halves have to agree: a mocked form with
 * real uploads would store a patient's insurance card against a submission that does not
 * exist, and a real form with mocked uploads would answer "uploaded" for bytes nobody
 * has. They are chosen together, once, at module load — the arrangement
 * `payments-client-context.tsx` established for the card form (BOOKING_FUNNEL_WIRING §6.6).
 *
 * | Build | Environment | Intake | Files |
 * |---|---|---|---|
 * | any | `VITE_USE_MOCKS=true` | the in-memory submission | the in-memory object store |
 * | `vite dev` only | `VITE_FAKE_UPLOADS=true` | the real API | the in-memory object store |
 * | `vite dev` / `vite build` | neither | the real API | the real API |
 *
 * The middle row is for a dev machine with no `STORAGE_DRIVER` configured: the form is
 * real, the answers are stored, and the two `file` fields answer from memory instead of
 * failing the step. It is compiled out of a bundle (`import.meta.env.DEV`), because a
 * production build that told a patient their insurance card was on file when nothing was
 * uploaded is the same lie about the same clinic that `payments.mock` refuses to tell.
 *
 * ## The default is inert
 *
 * The context's default value is `INERT_INTAKE`: every method rejects and none of them
 * makes a request. So a component rendered without a provider — every funnel parity test,
 * and `parity-dom.test.tsx` in particular — reaches nobody, and "does this page talk to
 * the intake API?" stays a property of the app shell that installs the runtime, exactly as
 * `useFunnelBoot` makes the draft transport a property of the route
 * (`Docs/BOOKING_FUNNEL_WIRING.md` §3.1).
 */

import { createContext, useContext, type ReactNode } from 'react';

import { publicFilesClient, patientFilesClient, type FilesClient } from './files';
import { intakeClient, type IntakeClient } from './intake';
import { createMockFilesClient, createMockIntakeClient } from './intake.mock';

export interface IntakeRuntime {
  intake: IntakeClient;
  /** The funnel's object store — owned by the `ha_draft` cookie. */
  funnelFiles: FilesClient;
  /** The signed-in patient's own. */
  patientFiles: FilesClient;
}

const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';
const fakeUploads = import.meta.env.DEV && import.meta.env.VITE_FAKE_UPLOADS === 'true';

function realFiles(): Pick<IntakeRuntime, 'funnelFiles' | 'patientFiles'> {
  if (!fakeUploads) return { funnelFiles: publicFilesClient, patientFiles: patientFilesClient };
  const fake = createMockFilesClient();
  return { funnelFiles: fake, patientFiles: fake };
}

/** The app's runtime, created once. The provider and the tests override it. */
export const intakeRuntime: IntakeRuntime = useMocks
  ? (() => {
      const files = createMockFilesClient();
      return { intake: createMockIntakeClient(), funnelFiles: files, patientFiles: files };
    })()
  : { intake: intakeClient, ...realFiles() };

/** Nothing installed: every call rejects, and no request is ever made. */
export const INERT_INTAKE: IntakeRuntime = {
  intake: {
    publishedForm: () => Promise.reject(new Error('intake is not installed')),
    currentFunnelPacket: () => Promise.resolve(null),
    startFunnelPacket: () => Promise.reject(new Error('intake is not installed')),
    saveFunnelAnswers: () => Promise.reject(new Error('intake is not installed')),
    completeFunnelIntake: () => Promise.reject(new Error('intake is not installed')),
    appointmentPacket: () => Promise.reject(new Error('intake is not installed')),
    saveAppointmentAnswers: () => Promise.reject(new Error('intake is not installed')),
  },
  funnelFiles: inertFiles(),
  patientFiles: inertFiles(),
};

function inertFiles(): FilesClient {
  const refuse = (): Promise<never> => Promise.reject(new Error('uploads are not installed'));
  return {
    requestUpload: refuse,
    putBytes: refuse,
    complete: refuse,
    downloadUrl: refuse,
    list: null,
    remove: null,
  };
}

const IntakeRuntimeContext = createContext<IntakeRuntime>(INERT_INTAKE);

export function IntakeRuntimeProvider({
  runtime,
  children,
}: {
  runtime: IntakeRuntime;
  children: ReactNode;
}) {
  return <IntakeRuntimeContext.Provider value={runtime}>{children}</IntakeRuntimeContext.Provider>;
}

export function useIntakeRuntime(): IntakeRuntime {
  return useContext(IntakeRuntimeContext);
}

/** The intake half on its own — the portal's queries want nothing else. */
export function useIntakeClient(): IntakeClient {
  return useIntakeRuntime().intake;
}
